import React from "react";
import { Toggle } from "@fluentui/react";
import { IdentityPersonalDataInputsProps } from "@/screen/Identities/IdentityPersonalDataInputs/Interfaces/IdentityPersonalDataInputsInterfaces";
import { FormSection, FormRow, FormField, FormLabel, FormInput, FormSelect, ToggleRow, ToggleRowLabel, SinglePhoneHelpText, InlineFieldError } from "@/screen/Identities/IdentityPersonalDataInputs/Styles/IdentityPersonalDataInputsStyles";
import CountryPrefixes from "@/utils/CountryPrefixes";
import { PersonNameMaxLength, PersonNameInvalidMessage, isValidPersonName } from "@/utils/personNameRules";
import { parseIsoDate, formatDateOnly } from "@/utils/DateFormatter";

const IdentityPersonalDataInputs: React.FC<IdentityPersonalDataInputsProps> = ({formData, validation, highlightPendingManualFields, isFormLocked, isIdTypeLocked, isEmailLocked, hasBirthdayBusinessError, hasIdExpirationBusinessError, onChange, onBirthdayChange, onIdExpirationChange, onPermanentIdExpirationToggle, onUseSinglePhoneToggle, countriesCatalog}) =>
{
    const ph = (ok: boolean): boolean => highlightPendingManualFields && !ok;

    // Un nombre escrito (o leído del DNI) que no cumple el formato se explica bajo el campo. Un nombre legacy
    // cargado tal cual no se marca: su validación llega a true desde la carga y el backend no lo revalida.
    const nameFormatError = (value: string | null | undefined, isValid: boolean): React.ReactNode =>
        !isValid && !!value?.trim() && !isValidPersonName(value) ? <InlineFieldError role="alert">{PersonNameInvalidMessage}</InlineFieldError> : null;

    return(
        <FormSection>
            <h3>Datos Personales</h3>
            {isFormLocked &&
            (
                <p style={{color: "#CC214F", marginBottom: "15px", fontStyle: "italic", cursor: "default"}}>
                    Procesa el documento de identidad para habilitar estos campos.
                </p>
            )}
            <FormRow>
                <FormField>
                    <FormLabel required>Nombre</FormLabel>
                    <FormInput type="text" name="firstName" value={formData.firstName} onChange={onChange}
                        placeholder="Nombre" disabled={isFormLocked} autoComplete="off" maxLength={PersonNameMaxLength}
                        $pendingHighlight={ph(validation.firstName)}
                    />
                    {nameFormatError(formData.firstName, validation.firstName)}
                </FormField>
                <FormField>
                    <FormLabel required>Primer Apellido</FormLabel>
                    <FormInput type="text" name="lastName" value={formData.lastName} onChange={onChange}
                        placeholder="Primer apellido" disabled={isFormLocked} autoComplete="off" maxLength={PersonNameMaxLength}
                        $pendingHighlight={ph(validation.lastName)}
                    />
                    {nameFormatError(formData.lastName, validation.lastName)}
                </FormField>
                <FormField>
                    <FormLabel required>Segundo Apellido</FormLabel>
                    <FormInput type="text" name="secondLastName" value={formData.secondLastName} onChange={onChange}
                        placeholder="Segundo apellido" disabled={isFormLocked} autoComplete="off" maxLength={PersonNameMaxLength}
                        $pendingHighlight={ph(validation.secondLastName)}
                    />
                    {nameFormatError(formData.secondLastName, validation.secondLastName)}
                </FormField>
            </FormRow>
            <FormRow>
                <FormField>
                    <FormLabel required>Tipo de documento</FormLabel>
                    <FormSelect name="idType" value={formData.idType} onChange={onChange} disabled={isFormLocked || isIdTypeLocked}
                        $pendingHighlight={ph(formData.idType === "DNI" || formData.idType === "NIE")}
                    >
                        {formData.idType === "" && <option value="">-- Seleccionar --</option>}
                        <option value="DNI">DNI</option>
                        <option value="NIE">NIE</option>
                    </FormSelect>
                </FormField>
                <FormField>
                    <FormLabel required>Número de documento</FormLabel>
                    <FormInput type="text" name="idDocument" value={formData.idDocument} onChange={onChange}
                        placeholder="12345678A" disabled={isFormLocked || isIdTypeLocked} autoComplete="off"
                        $pendingHighlight={ph(validation.idDocument)}
                    />
                    {!isFormLocked && isIdTypeLocked &&
                    (
                        <SinglePhoneHelpText>
                            El documento de identidad no puede modificarse una vez dado de alta el interviniente.
                            Si el número no es correcto, contacta con el soporte en el botón de &apos;Necesitas ayuda&apos;.
                        </SinglePhoneHelpText>
                    )}
                </FormField>
                <FormField>
                    <FormLabel required>Fecha de validez</FormLabel>
                    {/* Con validez permanente el campo se inutiliza pero conserva la fecha visible: así un
                        clic accidental en el check no borra lo que el cliente hubiera puesto. Lo que se
                        persiste lo resuelve el backend a partir del check, no de esta fecha. */}
                    <FormInput type="date" name="idExpiration" value={formatDateOnly(formData.idExpiration) ?? ""}
                        onChange={(e) =>
                        {
                            onIdExpirationChange(parseIsoDate(e.target.value));
                        }}
                        disabled={isFormLocked || formData.hasPermanentIdExpiration}
                        autoComplete="off"
                        $pendingHighlight={ph(validation.idExpiration)}
                        $errorHighlight={hasIdExpirationBusinessError}
                    />
                    <ToggleRow style={{marginTop: "8px", justifyContent: "flex-end"}}>
                        <ToggleRowLabel>Validez Permanente</ToggleRowLabel>
                        <Toggle ariaLabel="El documento de identidad tiene validez permanente"
                            checked={formData.hasPermanentIdExpiration}
                            onChange={onPermanentIdExpirationToggle} disabled={isFormLocked}
                        />
                    </ToggleRow>
                    {formData.hasPermanentIdExpiration &&
                    (
                        <SinglePhoneHelpText>
                            Si desmarcas la validez permanente, deberás indicar una fecha de validez.
                        </SinglePhoneHelpText>
                    )}
                </FormField>
            </FormRow>
            <FormRow>
                <FormField>
                    <FormLabel required>Fecha de nacimiento</FormLabel>
                    <FormInput type="date" name="birthday" value={formatDateOnly(formData.birthday) ?? ""}
                        onChange={(e) =>
                        {
                            onBirthdayChange(parseIsoDate(e.target.value));
                        }}
                        disabled={isFormLocked}
                        autoComplete="off"
                        $pendingHighlight={ph(validation.birthday)}
                        $errorHighlight={hasBirthdayBusinessError}
                    />
                </FormField>
                <FormField>
                    <FormLabel required>País de nacimiento</FormLabel>
                    <FormSelect name="birthdayCountry" value={formData.birthdayCountry} onChange={onChange} disabled={isFormLocked}
                        $pendingHighlight={ph(validation.birthdayCountry)}
                    >
                        {/* Sin la opción vacía, un país sin valor se pintaría como el primero del catálogo */}
                        {formData.birthdayCountry === "" && <option value="">-- Seleccionar --</option>}
                        {countriesCatalog.map((country) =>
                        (
                            <option key={`birth-country-${country.value}`} value={country.value}>{country.text}</option>
                        ))}
                    </FormSelect>
                </FormField>
                <FormField>
                    <FormLabel required>Nacionalidad</FormLabel>
                    <FormSelect name="nationalCountry" value={formData.nationalCountry} onChange={onChange} disabled={isFormLocked}
                        $pendingHighlight={ph(validation.nationalCountry)}
                    >
                        {formData.nationalCountry === "" && <option value="">-- Seleccionar --</option>}
                        {countriesCatalog.map((country) =>
                        (
                            <option key={`national-country-${country.value}`} value={country.value}>{country.text}</option>
                        ))}
                    </FormSelect>
                </FormField>
            </FormRow>
            {!formData.isMinor &&
            (
                <FormRow>
                    <FormField>
                        <FormLabel required>Correo electrónico</FormLabel>
                        <FormInput type="email" name="email" value={formData.email} onChange={onChange}
                            placeholder="correo@ejemplo.com" disabled={isFormLocked || isEmailLocked}
                            autoComplete="off"
                            $pendingHighlight={ph(validation.email)}
                        />
                        {!isFormLocked && isEmailLocked &&
                        (
                            <SinglePhoneHelpText>
                                El correo del interviniente principal es la credencial de acceso a la cuenta y no
                                puede modificarse desde aquí.
                            </SinglePhoneHelpText>
                        )}
                    </FormField>
                </FormRow>
            )}
            {!formData.isMinor &&
            (
                <React.Fragment>
                    <FormRow>
                        <FormField>
                            <FormLabel required>Prefijo del teléfono</FormLabel>
                            <FormSelect name="countryPrefix" value={formData.countryPrefix} onChange={onChange}
                                disabled={isFormLocked}
                                $pendingHighlight={ph(validation.countryPrefix)}
                            >
                                <option value="">-- Seleccionar --</option>
                                {CountryPrefixes.filter((c) => c.phone_code).map((c) =>
                                (
                                    <option key={`phone-${c.key}`} value={c.phone_code}>{c.name} - ({c.phone_code})</option>
                                ))}
                            </FormSelect>
                        </FormField>
                        <FormField>
                            <FormLabel required>Teléfono</FormLabel>
                            <FormInput type="tel" name="phone" value={formData.phone} onChange={onChange}
                                placeholder="666111222" disabled={isFormLocked}
                                autoComplete="off"
                                $pendingHighlight={ph(validation.phone)}
                            />
                        </FormField>
                    </FormRow>
                    {!formData.useSinglePhone &&
                    (
                        <React.Fragment>
                            <FormRow>
                                <FormField>
                                    <FormLabel required>Prefijo del Teléfono de Operaciones</FormLabel>
                                    <FormSelect name="operationCountryPrefix" value={formData.operationCountryPrefix} onChange={onChange}
                                        disabled={isFormLocked}
                                        $pendingHighlight={ph(validation.operationCountryPrefix)}
                                    >
                                        <option value="">-- Seleccionar --</option>
                                        {CountryPrefixes.filter((c) => c.phone_code).map((c) =>
                                        (
                                            <option key={`op-phone-${c.key}`} value={c.phone_code}>{c.name} - ({c.phone_code})</option>
                                        ))}
                                    </FormSelect>
                                </FormField>
                                <FormField>
                                    <FormLabel required>Teléfono de Operaciones</FormLabel>
                                    <FormInput type="tel" name="operationPhone" value={formData.operationPhone}
                                        onChange={onChange} placeholder="666111222" disabled={isFormLocked}
                                        autoComplete="off"
                                        $pendingHighlight={ph(validation.operationPhone)}
                                    />
                                </FormField>
                            </FormRow>
                            <FormRow>
                                <FormField>
                                    <FormLabel required>Prefijo del Teléfono de Firmas</FormLabel>
                                    <FormSelect name="signatureCountryPrefix" value={formData.signatureCountryPrefix}
                                        onChange={onChange} disabled={isFormLocked}
                                        $pendingHighlight={ph(validation.signatureCountryPrefix)}
                                    >
                                        <option value="">-- Seleccionar --</option>
                                        {CountryPrefixes.filter((c) => c.phone_code).map((c) =>
                                        (
                                            <option key={`sign-phone-${c.key}`} value={c.phone_code}>{c.name} - ({c.phone_code})</option>
                                        ))}
                                    </FormSelect>
                                </FormField>
                                <FormField>
                                    <FormLabel required>Teléfono de Firmas</FormLabel>
                                    <FormInput type="tel" name="signaturePhone" value={formData.signaturePhone}
                                        onChange={onChange} placeholder="666111222" disabled={isFormLocked}
                                        autoComplete="off"
                                        $pendingHighlight={ph(validation.signaturePhone)}
                                    />
                                </FormField>
                            </FormRow>
                        </React.Fragment>
                    )}
                    <FormRow>
                        <FormField style={{flex: "1"}}>
                            <ToggleRow>
                                <ToggleRowLabel>Uso un único teléfono</ToggleRowLabel>
                                <Toggle ariaLabel="Uso un único número de teléfono" checked={formData.useSinglePhone}
                                    onChange={onUseSinglePhoneToggle} disabled={isFormLocked}
                                />
                            </ToggleRow>
                            {formData.useSinglePhone &&
                            (
                                <SinglePhoneHelpText>
                                    El teléfono indicado debe poder recibir SMS para la correcta operativa del sistema.
                                </SinglePhoneHelpText>
                            )}
                        </FormField>
                    </FormRow>
                </React.Fragment>
            )}
        </FormSection>
    );
};

export default IdentityPersonalDataInputs;